import { useState, useEffect, useCallback, useRef } from 'react';
import { notificationsService } from '../services/notificationsService';

const PUSH_BROADCAST_CHANNEL = 'agendario-push';
const PUSH_ENDPOINT_KEY = 'agendario_push_endpoint'; // endpoint registrado com sucesso no backend

/**
 * Hook para gerenciar Web Push Notifications.
 *
 * Retorna:
 * - pushSupported: browser suporta Push API?
 * - permissionState: 'default' | 'granted' | 'denied' | null
 * - isSubscribed: este browser tem subscription ativa?
 * - isLoading: operacao em andamento?
 * - requestPermission(): solicitar permissao + subscribe
 * - unsubscribe(): cancelar subscription
 */
export function usePushNotifications() {
  const [pushSupported, setPushSupported] = useState(false);
  const [permissionState, setPermissionState] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const initRef = useRef(false);

  // Inicializar: verificar suporte, permissao e subscription existente
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      // Verificar suporte
      const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      setPushSupported(supported);

      if (!supported) return;

      // Registrar Service Worker (idempotente - se ja registrado, retorna existente)
      try {
        await navigator.serviceWorker.register('/sw.js');
      } catch (err) {
        console.warn('[PushNotifications] Erro ao registrar SW:', err);
        return;
      }

      // Ler estado de permissao atual
      const permission = Notification.permission;
      setPermissionState(permission);

      if (permission === 'granted') {
        // Verificar se este browser ja tem subscription ativa
        await checkExistingSubscription();
      }
    };

    init();
  }, []);

  const checkExistingSubscription = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const storedEndpoint = localStorage.getItem(PUSH_ENDPOINT_KEY);

        if (!storedEndpoint) {
          // Subscription existe no browser mas nunca foi registrada com sucesso no backend
          // (envio anterior falhou por erro de rede, 5xx, etc). Retenta agora.
          console.info('[PushNotifications] Subscription sem confirmação de registro no backend — retentando...');
          await subscribeToServer();
        } else if (storedEndpoint !== subscription.endpoint) {
          // Endpoint mudou: browser renovou a subscription enquanto a page estava fechada
          // (pushsubscriptionchange sem page aberta, camada de safety net).
          console.info('[PushNotifications] Endpoint mudou desde o último registro — re-inscrevendo no backend...');
          await subscribeToServer();
        } else {
          // Endpoint confirmado e inalterado — tudo certo.
          setIsSubscribed(true);
        }
      } else {
        // Permissao granted mas sem subscription - re-subscribe silenciosamente
        await subscribeToServer();
      }
    } catch (err) {
      console.warn('[PushNotifications] Erro ao verificar subscription:', err);
    }
  }, []);

  const subscribeToServer = useCallback(async () => {
    try {
      // Obter VAPID public key do servidor
      const vapidPublicKey = await notificationsService.getVapidPublicKey();
      if (!vapidPublicKey) {
        console.warn('[PushNotifications] VAPID key indisponivel');
        return false;
      }

      const registration = await navigator.serviceWorker.ready;

      // Criar subscription no browser
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Extrair keys
      const p256dh = arrayBufferToBase64(subscription.getKey('p256dh'));
      const auth = arrayBufferToBase64(subscription.getKey('auth'));

      // Enviar para o backend
      const subscriptionId = await notificationsService.subscribePush(
        subscription.endpoint,
        p256dh,
        auth
      );

      if (subscriptionId) {
        // Persistir endpoint para detectar renovações silenciosas no próximo carregamento
        localStorage.setItem(PUSH_ENDPOINT_KEY, subscription.endpoint);
        setIsSubscribed(true);
        return true;
      }

      return false;
    } catch (err) {
      console.warn('[PushNotifications] Erro ao subscribe:', err);
      return false;
    }
  }, []);

  // Ouvir sinalização do SW via BroadcastChannel e via postMessage (fallback).
  // Disparado quando a subscription expira/muda (pushsubscriptionchange no SW).
  // O SW não tem JWT, então delega o re-subscribe autenticado para cá.
  useEffect(() => {
    if (!pushSupported) return;

    const handleResubscribe = () => {
      console.info('[PushNotifications] SW sinalizou pushsubscriptionchange — re-inscrevendo...');
      subscribeToServer();
    };

    // Canal primário: BroadcastChannel
    let channel = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(PUSH_BROADCAST_CHANNEL);
      channel.onmessage = (event) => {
        if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
          handleResubscribe();
        }
      };
    }

    // Fallback redundante: ServiceWorker postMessage direto
    const onSwMessage = (event) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
        handleResubscribe();
      }
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);

    return () => {
      if (channel) channel.close();
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    };
  }, [pushSupported, subscribeToServer]);

  /**
   * Solicitar permissao ao usuario e subscribe se granted.
   * DEVE ser chamado a partir de uma acao do usuario (click).
   */
  const requestPermission = useCallback(async () => {
    if (!pushSupported) return false;

    setIsLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);

      if (permission === 'granted') {
        const success = await subscribeToServer();
        return success;
      }

      return false;
    } catch (err) {
      console.warn('[PushNotifications] Erro ao requestPermission:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [pushSupported, subscribeToServer]);

  /**
   * Cancelar subscription no browser e no backend.
   */
  const unsubscribe = useCallback(async () => {
    if (!pushSupported) return false;

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Notificar backend antes de cancelar no browser
        await notificationsService.unsubscribePushByEndpoint(subscription.endpoint);
        // Cancelar no browser
        await subscription.unsubscribe();
      }

      localStorage.removeItem(PUSH_ENDPOINT_KEY);
      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.warn('[PushNotifications] Erro ao unsubscribe:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [pushSupported]);

  return {
    pushSupported,
    permissionState,
    isSubscribed,
    isLoading,
    requestPermission,
    unsubscribe
  };
}

// --- Helpers ---

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
